import React, { useEffect, useRef, useState } from "react";
import "./ImagePreviewModal.css";
import { addComponentState, removeComponentState } from "../../core/Global";

type ImagePreviewModalProps = {
    id: string;
    src: string;
    alt?: string;
    /** A clip rather than a picture: played with the browser's own controls, not zoomed. */
    video?: boolean;
    onClose: () => void;
};

export const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
    id,
    src,
    alt = "Preview",
    video = false,
    onClose,
}) => {
    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });

    const dragging = useRef(false);
    const lastPoint = useRef({ x: 0, y: 0 });
    const lastDistance = useRef<number | null>(null);

    useEffect(() => {
        document.body.style.overflow = "hidden";

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [onClose]);

    const clampScale = (value: number) => {
        return Math.min(Math.max(value, 1), 5);
    };

    const handleWheel = (e: React.WheelEvent) => {
        e.preventDefault();

        setScale((prev) => {
            const next = clampScale(prev + (e.deltaY < 0 ? 0.2 : -0.2));

            if (next === 1) {
                setPosition({ x: 0, y: 0 });
            }

            return next;
        });
    };

    const startDrag = (x: number, y: number) => {
        dragging.current = true;
        lastPoint.current = { x, y };
    };

    const moveDrag = (x: number, y: number) => {
        if (!dragging.current || scale <= 1) return;

        const dx = x - lastPoint.current.x;
        const dy = y - lastPoint.current.y;

        setPosition((prev) => ({
            x: prev.x + dx,
            y: prev.y + dy,
        }));

        lastPoint.current = { x, y };
    };

    const stopDrag = () => {
        dragging.current = false;
        lastDistance.current = null;
    };

    const getTouchDistance = (touches: React.TouchList) => {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
    };


    useEffect(() => {

        addComponentState({
            id,
            close: onClose
        })

        return () => {
            removeComponentState(id);
        }

    }, [])


    if (video) {
        /* No zooming or dragging here: those handlers would take the clicks and touches the
           video's own controls need. A click outside the clip closes it, as with a picture. */
        return (
            <div
                className="image-preview-overlay"
                onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        onClose();
                    }
                }}
            >
                <button className="image-preview-close" onClick={onClose}>
                    ×
                </button>

                <video
                    src={src}
                    className="image-preview-img image-preview-video"
                    controls
                    autoPlay
                    playsInline
                    onClick={(e) => e.stopPropagation()}
                />
            </div>
        );
    }

    return (
        <div
            className="image-preview-overlay"
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
            onWheel={handleWheel}
            onMouseDown={(e) => startDrag(e.clientX, e.clientY)}
            onMouseMove={(e) => moveDrag(e.clientX, e.clientY)}
            onMouseUp={stopDrag}
            onMouseLeave={stopDrag}
            onTouchStart={(e) => {
                if (e.touches.length === 1) {
                    startDrag(e.touches[0].clientX, e.touches[0].clientY);
                }

                if (e.touches.length === 2) {
                    lastDistance.current = getTouchDistance(e.touches);
                }
            }}
            onTouchMove={(e) => {
                e.preventDefault();

                if (e.touches.length === 1) {
                    moveDrag(e.touches[0].clientX, e.touches[0].clientY);
                }

                if (e.touches.length === 2) {
                    const currentDistance = getTouchDistance(e.touches);

                    if (lastDistance.current) {
                        const diff = currentDistance - lastDistance.current;

                        setScale((prev) => {
                            const next = clampScale(prev + diff * 0.01);

                            if (next === 1) {
                                setPosition({ x: 0, y: 0 });
                            }

                            return next;
                        });
                    }

                    lastDistance.current = currentDistance;
                }
            }}
            onTouchEnd={stopDrag}
        >
            <button className="image-preview-close" onClick={onClose}>
                ×
            </button>

            <img
                src={src}
                alt={alt}
                draggable={false}
                className="image-preview-img"
                onClick={(e) => e.stopPropagation()}
                style={{
                    transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                    cursor: scale > 1 ? "grab" : "zoom-in",
                }}
                onDoubleClick={() => {
                    setScale((prev) => {
                        const next = prev === 1 ? 2 : 1;
                        if (next === 1) setPosition({ x: 0, y: 0 });
                        return next;
                    });
                }}
            />
        </div>
    );
};